import React from "react";
import { Plus } from "lucide-react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { formatUTCToIST } from "lib/utils";
import { CnLhcDetail } from "api/lhcUpdate";

interface LhcCnsTableProps {
  searchedLhc: string;
  cnLhcList: CnLhcDetail[];
  onOpenLinkModal: () => void;
}

export const LhcCnsTable: React.FC<LhcCnsTableProps> = ({
  searchedLhc,
  cnLhcList,
  onOpenLinkModal,
}) => {
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden space-y-0 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b bg-gray-50/50">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-900">LHC: {searchedLhc}</span>
          <Badge variant="outline" className="text-xs bg-white text-gray-700 font-medium">
            {cnLhcList.length} {cnLhcList.length === 1 ? "CN Linked" : "CNs Linked"}
          </Badge>
        </div>

        <Button onClick={onOpenLinkModal} size="sm" className="h-8 px-3 text-xs font-medium bg-black text-white hover:bg-gray-800 flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Link New CN Number
        </Button>
      </div>

      {cnLhcList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <p className="text-sm font-medium text-gray-700">
            No CN numbers currently linked to LHC <span className="font-semibold">{searchedLhc}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1 mb-4">
            Click below to link the first CN number to this LHC.
          </p>
          <Button
            onClick={onOpenLinkModal}
            size="sm"
            className="h-8 px-3 text-xs font-medium bg-black text-white hover:bg-gray-800 flex items-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Link New CN Number
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-gray-700">
            <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider text-[11px] font-semibold border-b">
              <tr>
                <th className="py-3 px-6 w-16">#</th>
                <th className="py-3 px-6">CN Number</th>
                <th className="py-3 px-6">From Date / Time</th>
                <th className="py-3 px-6">To Date / Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cnLhcList.map((item, index) => (
                <tr key={`${item.cn_number}-${index}`} className="hover:bg-gray-50/80 transition-colors">
                  <td className="py-3.5 px-6 font-medium text-gray-400">{index + 1}</td>
                  <td className="py-3.5 px-6 font-semibold text-gray-900">{item.cn_number}</td>
                  <td className="py-3.5 px-6 font-normal text-gray-600">
                    {item.from_datetime ? formatUTCToIST(item.from_datetime) : "—"}
                  </td>
                  <td className="py-3.5 px-6 font-normal text-gray-600">
                    {item.to_datetime ? formatUTCToIST(item.to_datetime) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
